/**
 * Origem Flow: captura de tarefa em linguagem natural (pt-BR), determinística
 * e sem IA — "Relatório mensal @Lumar sex 15h p1 para Mari".
 *
 * Reconhece, em qualquer ordem:
 *   @Tabela            → tabela/cliente (prefixo do nome, sem acento/caixa)
 *   hoje, amanhã, depois de amanhã, seg…dom, segunda…domingo, 15/09,
 *   15/09/2026, dia 15, 15 de setembro, próxima sexta
 *                      → data (sempre a próxima ocorrência)
 *   15h, 15h30, 15:30  → hora
 *   p1…p4, !urgente, !alta, !média, !baixa, urgente
 *                      → prioridade (p1 Urgente, p2 Alta, p3 Média, p4 Baixa)
 *   para Nome, p/ Nome → responsável (prefixo do primeiro nome)
 * O que sobra é o título. Tudo que foi interpretado volta em `chips` para
 * a interface mostrar antes de salvar.
 */
import { normalize } from '@baserow/modules/core/search/commandActions'

const WEEKDAYS = [
  ['domingo', 'dom'],
  ['segunda', 'seg', 'segunda-feira'],
  ['terca', 'ter', 'terca-feira'],
  ['quarta', 'qua', 'quarta-feira'],
  ['quinta', 'qui', 'quinta-feira'],
  ['sexta', 'sex', 'sexta-feira'],
  ['sabado', 'sab'],
]

const MONTHS = [
  'janeiro',
  'fevereiro',
  'marco',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
]

const PRIORITY_CODES = {
  p1: 'urgente',
  p2: 'alta',
  p3: 'media',
  p4: 'baixa',
}
const PRIORITY_WORDS = new Set(['urgente', 'alta', 'media', 'baixa'])

function pad(n) {
  return String(n).padStart(2, '0')
}

function toISODate(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function formatBR(date) {
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`
}

function addDays(base, days) {
  const date = new Date(base.getFullYear(), base.getMonth(), base.getDate())
  date.setDate(date.getDate() + days)
  return date
}

function nextWeekday(base, weekday, forceNextWeek = false) {
  let delta = (weekday - base.getDay() + 7) % 7
  if (delta === 0 || forceNextWeek) {
    delta += delta === 0 ? 7 : 0
    if (forceNextWeek && delta < 7) {
      delta += 7
    }
  }
  return addDays(base, delta)
}

/**
 * Procura o nome (tabela/usuário) cujo nome normalizado começa pelas
 * palavras digitadas; tenta do maior número de palavras para o menor, para
 * "@app sj da barra" casar com "App S.J. da Barra".
 */
function matchByPrefix(words, startIndex, candidates, nameOf) {
  for (let len = Math.min(4, words.length - startIndex); len >= 1; len--) {
    const needle = normalize(
      words.slice(startIndex, startIndex + len).join(' ')
    )
    if (!needle) {
      continue
    }
    const found = candidates.filter((candidate) => {
      const name = normalize(nameOf(candidate)).replace(/[^a-z0-9 ]/g, '')
      return name.startsWith(needle.replace(/[^a-z0-9 ]/g, ''))
    })
    if (found.length >= 1) {
      // Preferência pelo nome mais curto (casamento mais específico).
      found.sort((a, b) => nameOf(a).length - nameOf(b).length)
      return { match: found[0], consumed: len }
    }
  }
  return null
}

/**
 * @param {string} text
 * @param {{ tables?: Array<{id:number,name:string}>, users?: Array<{id?:number,user_id?:number,name:string}>, now?: Date }} context
 */
export function parseNaturalTask(text, context = {}) {
  const now = context.now || new Date()
  const tables = context.tables || []
  const users = context.users || []
  const words = (text || '').trim().split(/\s+/).filter(Boolean)
  const result = {
    title: '',
    table: null,
    date: null,
    time: null,
    priority: null,
    assignee: null,
    chips: [],
  }
  if (words.length === 0) {
    return result
  }

  const used = new Array(words.length).fill(false)
  const take = (index, count = 1) => {
    for (let i = index; i < index + count; i++) {
      used[i] = true
    }
  }

  for (let i = 0; i < words.length; i++) {
    if (used[i]) {
      continue
    }
    const raw = words[i]
    const word = normalize(raw)

    // @Tabela
    if (raw.startsWith('@') && raw.length > 1 && !result.table) {
      const rest = [raw.slice(1), ...words.slice(i + 1)]
      const match = matchByPrefix(rest, 0, tables, (table) => table.name)
      if (match) {
        result.table = match.match
        take(i, match.consumed)
        continue
      }
    }

    // Prioridade: p1..p4, !palavra, "urgente" solto
    if (PRIORITY_CODES[word] && !result.priority) {
      result.priority = PRIORITY_CODES[word]
      take(i)
      continue
    }
    if (
      raw.startsWith('!') &&
      PRIORITY_WORDS.has(word.slice(1)) &&
      !result.priority
    ) {
      result.priority = word.slice(1)
      take(i)
      continue
    }
    if (word === 'urgente' && !result.priority) {
      result.priority = 'urgente'
      take(i)
      continue
    }

    // Responsável: "para Nome" / "p/ Nome"
    if (
      (word === 'para' || word === 'p/') &&
      i + 1 < words.length &&
      !result.assignee
    ) {
      const match = matchByPrefix(words, i + 1, users, (user) => user.name)
      if (match) {
        result.assignee = match.match
        take(i, match.consumed + 1)
        continue
      }
    }

    // Hora: 15h, 15h30, 15:30, 9h
    const timeMatch = word.match(/^(\d{1,2})(?:h|:)(\d{2})?$/)
    if (timeMatch && !result.time && parseInt(timeMatch[1]) < 24) {
      result.time = `${pad(timeMatch[1])}:${timeMatch[2] || '00'}`
      take(i)
      continue
    }

    if (result.date) {
      continue
    }

    // Datas relativas
    if (word === 'hoje') {
      result.date = addDays(now, 0)
      take(i)
      continue
    }
    if (word === 'amanha') {
      result.date = addDays(now, 1)
      take(i)
      continue
    }
    if (
      word === 'depois' &&
      normalize(words[i + 1] || '') === 'de' &&
      normalize(words[i + 2] || '') === 'amanha'
    ) {
      result.date = addDays(now, 2)
      take(i, 3)
      continue
    }
    // "próxima sexta" / "próximo sábado"
    let forceNext = false
    let dayWord = word
    let consumed = 1
    if ((word === 'proxima' || word === 'proximo') && i + 1 < words.length) {
      forceNext = true
      dayWord = normalize(words[i + 1])
      consumed = 2
    }
    const weekday = WEEKDAYS.findIndex((names) => names.includes(dayWord))
    if (
      weekday !== -1 &&
      (consumed === 2 || dayWord.length > 2 || WEEKDAYS[weekday][1] === dayWord)
    ) {
      result.date = nextWeekday(now, weekday, forceNext)
      take(i, consumed)
      continue
    }
    // dd/mm ou dd/mm/aaaa
    const dm = raw.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/)
    if (dm) {
      const day = parseInt(dm[1])
      const month = parseInt(dm[2]) - 1
      let year = dm[3] ? parseInt(dm[3]) : now.getFullYear()
      if (year < 100) {
        year += 2000
      }
      const date = new Date(year, month, day)
      if (date.getMonth() === month) {
        // Sem ano: se já passou, é o ano que vem.
        if (!dm[3] && date < addDays(now, 0)) {
          date.setFullYear(year + 1)
        }
        result.date = date
        take(i)
        continue
      }
    }
    // "dia 15" / "15 de setembro"
    if (word === 'dia' && /^\d{1,2}$/.test(words[i + 1] || '')) {
      const day = parseInt(words[i + 1])
      let date = new Date(now.getFullYear(), now.getMonth(), day)
      if (date < addDays(now, 0)) {
        date = new Date(now.getFullYear(), now.getMonth() + 1, day)
      }
      result.date = date
      take(i, 2)
      continue
    }
    if (/^\d{1,2}$/.test(word) && normalize(words[i + 1] || '') === 'de') {
      const month = MONTHS.indexOf(normalize(words[i + 2] || ''))
      if (month !== -1) {
        const day = parseInt(word)
        let date = new Date(now.getFullYear(), month, day)
        if (date < addDays(now, 0)) {
          date = new Date(now.getFullYear() + 1, month, day)
        }
        result.date = date
        take(i, 3)
        continue
      }
    }
  }

  result.title = words
    .filter((_, index) => !used[index])
    .join(' ')
    .replace(/\s+,/g, ',')
    .trim()

  if (result.table) {
    result.chips.push({ kind: 'table', label: result.table.name })
  }
  if (result.date) {
    result.chips.push({
      kind: 'date',
      label: result.time
        ? `${formatBR(result.date)} ${result.time}`
        : formatBR(result.date),
    })
  } else if (result.time) {
    result.chips.push({ kind: 'time', label: result.time })
  }
  if (result.priority) {
    const labels = {
      urgente: 'Urgente',
      alta: 'Alta',
      media: 'Média',
      baixa: 'Baixa',
    }
    result.chips.push({ kind: 'priority', label: labels[result.priority] })
  }
  if (result.assignee) {
    result.chips.push({ kind: 'assignee', label: result.assignee.name })
  }
  return result
}

/**
 * Serializa o que o parser entendeu num formato simples para viajar até a
 * tabela (evento de bus ou criação pendente) e virar valores de campo lá.
 */
export function toTaskIntent(parsed) {
  return {
    title: parsed.title,
    tableId: parsed.table ? parsed.table.id : null,
    date: parsed.date ? toISODate(parsed.date) : null,
    time: parsed.time,
    priority: parsed.priority,
    assigneeId: parsed.assignee
      ? (parsed.assignee.user_id ?? parsed.assignee.id)
      : null,
    assigneeName: parsed.assignee ? parsed.assignee.name : null,
  }
}
